import { supabase } from "@/lib/supabase";

export const FIREFLY_MOTION_SPEED_ACTION =
  "/api/admin/action?action=firefly-motion-speed";

export const FIREFLY_MOTION_SPEED_CHANGED_EVENT =
  "bb-firefly-motion-speed-changed";

export type FireflyMotionSpeedSaveResult =
  | { ok: true }
  | { ok: false; error: string };

export type FireflyMotionSpeedResponse = {
  ok: true;
  motion_speed_multiplier: number;
  source: "platform" | "default";
};

export function notifyFireflyMotionSpeedChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(FIREFLY_MOTION_SPEED_CHANGED_EVENT));
}

async function authHeaders(): Promise<Record<string, string> | null> {
  if (!supabase) return null;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
}

export async function fetchFireflyMotionSpeedMultiplier(): Promise<FireflyMotionSpeedResponse | null> {
  const headers = await authHeaders();
  if (!headers) return null;
  try {
    const r = await fetch(FIREFLY_MOTION_SPEED_ACTION, { headers });
    const json = (await r.json()) as FireflyMotionSpeedResponse & {
      ok?: boolean;
      error?: string;
    };
    if (!r.ok || !json.ok) return null;
    return json;
  } catch {
    return null;
  }
}

export async function saveFireflyMotionSpeedMultiplier(
  motionSpeedMultiplier: number,
): Promise<FireflyMotionSpeedSaveResult> {
  const headers = await authHeaders();
  if (!headers) {
    return { ok: false, error: "Sign in to save Firefly motion speed." };
  }
  const r = await fetch(FIREFLY_MOTION_SPEED_ACTION, {
    method: "PUT",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ motion_speed_multiplier: motionSpeedMultiplier }),
  });
  const json = (await r.json()) as FireflyMotionSpeedResponse & {
    ok?: boolean;
    error?: string;
  };
  if (!r.ok || !json.ok) {
    return {
      ok: false,
      error:
        typeof json.error === "string" ? json.error : `HTTP ${r.status}`,
    };
  }
  notifyFireflyMotionSpeedChanged();
  return { ok: true };
}
